// This component owns only local UI state: drawer visibility, search input, checkout flow,
// and delete dialog. All Redux access and navigation live in useMiscWorkQueue, which is
// consumed here as `wq`. Tests mock the hook rather than the Redux store or router.
import React, { useState, useEffect, useMemo, useRef } from 'react'
import { Link } from 'react-router-dom'
import WorkQueueActionCell from '../MyWorkQueue/WorkQueueActionCell'
import MiscRecordsModuleDropdown from './MiscRecordsModuleDropdown'
import { Spinner } from '../Loaders/Loaders'
import { PageError } from '../PageError/PageError'
import EmptyBox from '../MyRecords/EmptyBox'
import { Body, Drawer } from '../Shell/Shell'
import { ConfirmDialog } from '../Modal/Modal'
import { Report } from '../MyWorkQueue/ReportsList'
import Portal from 'components/Portal/Portal'
import FilterPopout from '../Table/FilterPopout'
import SearchIcon from '@tecuity/sos-icons/react/search-heavy'
import FilterIcon from '@tecuity/sos-icons/react/filter'
import CrossIcon from '@tecuity/sos-icons/react/cross-heavy'
import { noop, calculateReceiptItemContainersTotal } from 'utilities'
import * as api from '../../requests'
import ConnectedCheckout from 'components/Checkout/Checkout'
import SlidingPanel from 'components/SlidingPanel/SlidingPanel'
import { useMiscWorkQueue } from './useMiscWorkQueue'
import './MiscellaneousMyWorkQueue.scss'

const MY_WORK_QUEUE_SEARCH_PLACEHOLDER = process.env.MY_WORK_QUEUE_SEARCH_PLACEHOLDER
const MY_WORK_QUEUE_EMPTY_STATE_LINE_1 = process.env.MY_WORK_QUEUE_EMPTY_STATE_LINE_1
const MY_WORK_QUEUE_EMPTY_STATE_LINE_2 = process.env.MY_WORK_QUEUE_EMPTY_STATE_LINE_2
const WQ_CONFIRM_ON_DELETE = process.env.WQ_CONFIRM_ON_DELETE
const WQ_DELETE_CONFIRM_MESSAGE = process.env.WQ_DELETE_CONFIRM_MESSAGE

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 539, label: 'New' },
  { value: 546, label: 'Pending' },
  { value: 558, label: 'Pending Review' },
  { value: 559, label: 'Needs Correction' },
  { value: 549, label: 'Approved' },
  { value: 540, label: 'Rejected' },
  { value: 560, label: 'Draft' },
  { value: 535, label: 'Pending Online Payment' },
]

const FILTER_TEMPLATES = [
  {
    type: 'select',
    name: 'status',
    label: 'Status',
    defaultValue: '',
    options: STATUS_OPTIONS,
  },
  {
    type: 'dateRange',
    name: 'submissionDate',
    label: 'Submission Date',
    defaultValue: { start: '', end: '' },
  },
]

const formatCellValue = (value, type) => {
  if (value == null) return '—'
  if (type === 'date') return new Date(value).toLocaleDateString()
  return value
}

const MiscReportsList = ({ filingId }) => {
  const [reports, setReports] = useState([])
  const [loading, setLoading] = useState(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!filingId) {
      setReports([])
      setLoading(null)
      setError(false)
      return
    }
    setLoading(true)
    setError(false)
    api.GET_MISC_FILING_REPORTS(
      filingId,
      (data) => {
        setReports(!data || !Array.isArray(data) ? [] : data)
        setLoading(false)
      },
      () => {
        setReports([])
        setLoading(false)
        setError(true)
      }
    )
  }, [filingId])

  if (loading === null) {
    return <div className="reports-list"><h5>No available downloads</h5></div>
  }
  if (loading) {
    return (
      <div className="reports-spinner animated fadeIn">
        <Spinner size="medium" themeColor="secondary" />
      </div>
    )
  }
  if (error) {
    return (
      <div className="reports-list">
        <h5>Something went wrong while loading the documents. Try again later.</h5>
      </div>
    )
  }
  return (
    <div className="reports-list">
      <h5>{reports.length > 0 ? 'Available Downloads:' : 'No available downloads'}</h5>
      {reports.map((report, i) => (
        <Report report={report} id={filingId} key={'' + filingId + i} delay={50 * i} />
      ))}
    </div>
  )
}

const FilterChip = ({ label, value, name, onRemove }) => (
  <span className="filter-chip">
    <div className="label">{label}</div>
    <div className="value">{value}</div>
    <button
      type="button"
      aria-label={'Remove ' + label + ' filter'}
      onClick={() => onRemove(name)}
    >
      <div className="center">
        <CrossIcon />
      </div>
    </button>
  </span>
)

const MiscellaneousMyWorkQueue = ({ sublinks }) => {
  const wq = useMiscWorkQueue({ sublinks })

  const [searchInput, setSearchInput] = useState('')
  const [filterOpen, setFilterOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingId, setDeletingId] = useState(null)
  const [downloadsItem, setDownloadsItem] = useState(null)
  const [checkoutOpen, setCheckoutOpen] = useState(false)
  const [checkoutDetails, setCheckoutDetails] = useState(null)
  const [paymentDisabled, setPaymentDisabled] = useState(false)
  const [paymentDisabledId, setPaymentDisabledId] = useState(null)
  const sliderRef = useRef(null)

  // Lives here rather than in the hook because it must also clear local drawer state.
  // The hook's refreshQueue only dispatches the fetch; clearing downloadsItem is this
  // component's responsibility since no other consumer of the hook has that state.
  useEffect(() => {
    setDownloadsItem(null)
    wq.refreshQueue()
  }, [wq.cartQuantity])

  const popoutFilters = useMemo(
    () => ({
      status: (wq.filters && wq.filters.status) || '',
      submissionDate: {
        start: (wq.filters && wq.filters.submissionStartDate) || '',
        end: (wq.filters && wq.filters.submissionEndDate) || '',
      },
    }),
    [wq.filters],
  )

  const firstCol = useMemo(
    () => (wq.template || []).filter((col) => !col.ChildOfKey)[0] || null,
    [wq.template],
  )

  const firstColChildren = useMemo(
    () => (firstCol ? (wq.template || []).filter((col) => col.ChildOfKey === firstCol.Key) : []),
    [wq.template, firstCol],
  )

  const hasActiveFilters =
    searchInput !== '' ||
    !!(wq.filters && (wq.filters.search || wq.filters.status || wq.filters.submissionStartDate || wq.filters.submissionEndDate))

  const filterChips = useMemo(() => {
    const chips = []
    if (wq.filters && wq.filters.search) {
      chips.push({ name: 'search', label: 'Search', value: wq.filters.search })
    }
    if (wq.filters && wq.filters.status) {
      const opt = STATUS_OPTIONS.find((o) => String(o.value) === String(wq.filters.status))
      chips.push({ name: 'status', label: 'Status', value: (opt && opt.label) || String(wq.filters.status) })
    }
    if (wq.filters && (wq.filters.submissionStartDate || wq.filters.submissionEndDate)) {
      const start = wq.filters.submissionStartDate ? new Date(wq.filters.submissionStartDate).toLocaleDateString() : ''
      const end = wq.filters.submissionEndDate ? new Date(wq.filters.submissionEndDate).toLocaleDateString() : ''
      const dateValue = start && end ? (start + ' – ' + end) : start ? ('From ' + start) : ('To ' + end)
      chips.push({ name: 'submissionDate', label: 'Submission Date', value: dateValue })
    }
    return chips
  }, [wq.filters])

  const handleSearch = () => {
    wq.search(searchInput)
  }

  const handleFilterChange = (newFilters, props) => {
    wq.applyFilters(newFilters, props)
  }

  const toggleFilter = () => {
    setFilterOpen((open) => !open)
  }

  const handleResetFilters = () => {
    setSearchInput('')
    wq.resetFilters()
  }

  const handleRemoveChip = (name) => {
    wq.removeFilter(name)
  }

  const handleDeleteDraft = (row) => {
    wq.deleteDraft(row.FilingId)
  }

  const handleDelete = (row) => {
    wq.deleteFiling(row.FilingId)
  }

  const handleConfirmDelete = (row) => {
    setDeletingId(row.FilingId)
    setDeleteDialogOpen(true)
  }

  const handleDeleteById = () => {
    wq.deleteDraft(deletingId, closeDeleteDialog)
  }

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false)
    setDeletingId(null)
  }

  const handleSelectDownloads = (row) => {
    if (downloadsItem && downloadsItem.FilingId === row.FilingId) {
      setDownloadsItem(null)
    } else {
      setDownloadsItem(row)
    }
  }

  const handleDismissDownloads = () => {
    setDownloadsItem(null)
  }

  const disablePaymentButtons = (id) => {
    setPaymentDisabled(true)
    setPaymentDisabledId(id)
  }

  const enablePaymentButtons = () => {
    setPaymentDisabled(false)
    setPaymentDisabledId(null)
  }

  const handleCloseCheckout = () => {
    setCheckoutOpen(false)
    enablePaymentButtons()
  }

  const handleCancelCheckout = () => {
    if (sliderRef.current) {
      sliderRef.current.closeSlider(() => {
        handleCloseCheckout()
      })
    } else {
      handleCloseCheckout()
    }
  }

  const handleOpenReceipt = (refNum) => {
    const ref = refNum || (checkoutDetails && checkoutDetails.refNum)
    if (sliderRef.current) {
      sliderRef.current.closeSlider(() => {
        wq.navigate('/receipt?ref=' + ref)
      })
    } else {
      wq.navigate('/receipt?ref=' + ref)
    }
  }

  const handleCartRefresh = () => {
    api.GET_WEB_USER_ACTIVE_CART((cart) => {
      setCheckoutDetails((prev) => ({
        ...prev,
        receiptItemContainers: cart.RECEIPT_ITEM_CONTAINER_WEB_LIST,
        receiptItems: [],
      }))
      wq.updateCartQuantity(cart.RECEIPT_ITEM_CONTAINER_WEB_LIST)
    })
    wq.refreshQueue()
  }

  const handleStartCheckout = (row) => {
    const filingNum = row.FilingNum
    const formDefinitionId = row.FormDefinitionId
    disablePaymentButtons(filingNum)

    api.GET_RECEIPT_ITEMS_BY_ADMENDMENT_NUM(
      filingNum,
      'miscellaneous',
      false,
      ({ RECEIPT_ITEM_CONTAINER_WEB_LIST, REF_NUM }) => {
        const total = calculateReceiptItemContainersTotal(RECEIPT_ITEM_CONTAINER_WEB_LIST)

        setCheckoutOpen(total > 0)
        setCheckoutDetails({
          amendmentNum: filingNum,
          formDefinitionId,
          receiptItems: [],
          receiptItemContainers: RECEIPT_ITEM_CONTAINER_WEB_LIST,
          refNum: REF_NUM,
        })

        wq.updateCartQuantity(RECEIPT_ITEM_CONTAINER_WEB_LIST)
        wq.refreshQueue()

        if (total <= 0) {
          handleOpenReceipt(REF_NUM)
        }
      },
      (error) => {
        enablePaymentButtons()
        wq.reportError(error && error.message)
        wq.refreshQueue()
      }
    )
  }

  const renderContent = () => {
    if (wq.loading) return <Spinner />
    if (wq.error) return <PageError />
    if (!wq.items || !wq.items.length) {
      const selectedModule =
        wq.modules.find((m) => m.MiscellaneousModuleId == wq.selectedModuleId) || wq.modules[0]
      const moduleName = (selectedModule && selectedModule.DisplayName) || ''
      const line1 = MY_WORK_QUEUE_EMPTY_STATE_LINE_1
        ? MY_WORK_QUEUE_EMPTY_STATE_LINE_1.replace(/\{\{CATEGORY\}\}/g, moduleName)
        : `Looks like you don't have any ${moduleName} records in progress`
      const line2 = MY_WORK_QUEUE_EMPTY_STATE_LINE_2
        ? MY_WORK_QUEUE_EMPTY_STATE_LINE_2.replace(/\{\{CATEGORY\}\}/g, moduleName)
        : `Visit ${moduleName} forms to create your first record`
      return (
        <div className="empty-placeholder-wrapper">
          <EmptyBox />
          <h2>{line1}</h2>
          <p>{line2}</p>
          <div className="flex-row center">
            <Link
              className="btn btn-primary btn-raised"
              to={`/forms/${wq.sublink.parameter}`}
            >
              Go to {moduleName} forms
            </Link>
          </div>
        </div>
      )
    }

    const parentCols = wq.template.filter((col) => !col.ChildOfKey)
    const childCols = wq.template.filter((col) => col.ChildOfKey)

    return (
      <div className="table-wrapper">
        <div className="row-count" role="status" aria-live="assertive" aria-atomic="true">
          <div className="row-count-text">{`Results: ${wq.items.length}`}</div>
        </div>
        <table className="div-table center-container" role="table">
          <thead className="div-table-heading">
            <tr role="row">
              {parentCols.map((col) => (
                <th key={col.Key} className="div-table-head" role="columnheader">
                  <div className="flex-row">
                    <span className="header-label">{col.Label}</span>
                  </div>
                </th>
              ))}
              <th className="div-table-head" role="columnheader">Actions</th>
            </tr>
          </thead>
          <tbody className="div-table-body">
            {wq.items.map((item) => (
              <tr
                key={item.FilingId}
                className={`div-table-row${downloadsItem && downloadsItem.FilingId === item.FilingId ? ' selected' : ''}`}
                role="row"
              >
                {parentCols.map((col, colIndex) => {
                  const children = childCols.filter((c) => c.ChildOfKey === col.Key)
                  const isFirst = colIndex === 0
                  const isSelected = isFirst && downloadsItem && downloadsItem.FilingId === item.FilingId
                  const cellContent = (
                    <>
                      <span className="cell">{formatCellValue(item[col.Key], col.Type)}</span>
                      {children.map((child) => (
                        <div key={child.Key} className="wq-cell-child">
                          <span className="cell">
                            {formatCellValue(item[child.Key], child.Type)}
                          </span>
                        </div>
                      ))}
                    </>
                  )
                  return (
                    <td
                      key={col.Key}
                      className={`div-table-cell${isFirst ? ' interactive' : ''}${isSelected ? ' selected' : ''}`}
                      role="gridcell"
                    >
                      {isFirst ? (
                        <>
                          <div
                            role="button"
                            tabIndex={0}
                            className="interactive-cell-button"
                            onClick={() => handleSelectDownloads(item)}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleSelectDownloads(item)}
                          >
                            {cellContent}
                          </div>
                          <span className="icon icon-caret-right" />
                        </>
                      ) : cellContent}
                    </td>
                  )
                })}
                <td className="div-table-cell no-padding action-cell" role="gridcell">
                  <WorkQueueActionCell
                    row={item}
                    statusKey="Status"
                    onView={wq.handleView}
                    onEditDraft={wq.handleEditDraft}
                    onCorrect={wq.handleCorrect}
                    onDelete={WQ_CONFIRM_ON_DELETE ? handleConfirmDelete : handleDelete}
                    onDeleteDraft={handleDeleteDraft}
                    onSelectDownloads={handleSelectDownloads}
                    onPayment={handleStartCheckout}
                    paymentDisabled={paymentDisabled}
                    paymentDisabledId={paymentDisabledId}
                    checkoutOpen={checkoutOpen}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <Body showDrawer={!!downloadsItem}>
      <div className="misc-work-queue">
        <div className="table-controls-wrapper">
          <div className="toolbar">
            <div className="misc-wq-module">
              <div className="misc-wq-module-inner">
                <MiscRecordsModuleDropdown modules={wq.modules} sourceTypeParam={wq.sourceTypeParam} showLabel={true} />
              </div>
            </div>
            {wq.sublink.label && (
              <h2>
                {`My ${wq.sublink.label} Work Queue`}
                {wq.loading && (
                  <div className="loading-wrapper">
                    <Spinner size="small" themeColor="medium-neutral" />
                  </div>
                )}
              </h2>
            )}
            <div className="filter-wrapper">
              <input
                type="text"
                className="filter"
                placeholder={MY_WORK_QUEUE_SEARCH_PLACEHOLDER || 'Search My Work Queue'}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                aria-label="Search"
              />
              <button
                type="button"
                aria-disabled={searchInput.length === 0}
                className="search btn btn-primary btn-raised"
                aria-label="Search"
                onClick={handleSearch}
              >
                <SearchIcon alt="Search Icon" />
              </button>
              <button
                type="button"
                className="search btn btn-primary btn-raised"
                aria-label="Filter"
                onClick={toggleFilter}
              >
                <FilterIcon alt="Filter icon" aria-hidden className="filter-icon" />
              </button>
              {hasActiveFilters && (
                <button
                  type="button"
                  className="search btn btn-primary btn-raised"
                  aria-label="Reset Filters"
                  onClick={handleResetFilters}
                >
                  <CrossIcon alt="Reset filters" aria-hidden />
                </button>
              )}
              {filterOpen && (
                <FilterPopout
                  onChange={handleFilterChange}
                  filterTemplates={FILTER_TEMPLATES}
                  filters={popoutFilters}
                  onRequestClose={toggleFilter}
                />
              )}
            </div>
          </div>
          {filterChips.length > 0 && (
            <div className="filter-chips misc-wq-filter-chips">
              {filterChips.map((chip) => (
                <FilterChip
                  key={chip.name}
                  label={chip.label}
                  value={chip.value}
                  name={chip.name}
                  onRemove={handleRemoveChip}
                />
              ))}
            </div>
          )}
        </div>
        {renderContent()}
      </div>
      <Drawer
        showCloseButton
        show={!!downloadsItem}
        title={downloadsItem && firstCol ? formatCellValue(downloadsItem[firstCol.Key], firstCol.Type) : ''}
        subtitle={
          downloadsItem && firstColChildren.length > 0
            ? firstColChildren.map((child) => formatCellValue(downloadsItem[child.Key], child.Type)).join(' · ')
            : undefined
        }
        onSidebarDismiss={handleDismissDownloads}
      >
        <MiscReportsList filingId={downloadsItem && downloadsItem.FilingId} />
      </Drawer>
      <Portal isOpened={checkoutOpen}>
        <SlidingPanel
          closeOnShadeClick={false}
          closeSlider={handleCloseCheckout}
          ref={sliderRef}
        >
          <ConnectedCheckout
            cancelCheckout={handleCancelCheckout}
            closeModal={handleCloseCheckout}
            redirectToReceipt={handleOpenReceipt}
            amendmentNum={checkoutOpen && checkoutDetails && checkoutDetails.amendmentNum}
            refNum={checkoutOpen && checkoutDetails && checkoutDetails.refNum}
            formDefinitionId={checkoutOpen && checkoutDetails && checkoutDetails.formDefinitionId}
            category='miscellaneous'
            receiptItems={[]}
            receiptItemContainers={(checkoutOpen && checkoutDetails && checkoutDetails.receiptItemContainers) || []}
            onReceiptItemContainerRemoved={wq.refreshQueue}
            onCartRefresh={handleCartRefresh}
          />
        </SlidingPanel>
      </Portal>
      <Portal isOpened={deleteDialogOpen}>
        <ConfirmDialog
          closeModal={closeDeleteDialog}
          message={
            WQ_DELETE_CONFIRM_MESSAGE
              ? WQ_DELETE_CONFIRM_MESSAGE
              : 'Are you sure you want to delete this filing?'
          }
          buttons={[
            { label: 'No', onClick: noop, className: 'btn-default' },
            { label: 'Yes', onClick: handleDeleteById, className: 'btn-danger' },
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
    </Body>
  )
}

export default MiscellaneousMyWorkQueue
